import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import Shield2 from "../../../assets/aftersalesservice_icons/shield2.svg";
import HelpCenter from "../../../assets/aftersalesservice_icons/help_center.svg";
import WarrantyStatus from "../../../assets/aftersalesservice_icons/warranty_status.svg";
import PointRight from "../../../assets/aftersalesservice_icons/pointright_icon.svg";
import Policy from "../../../assets/aftersalesservice_icons/policy.svg";
import UserManual from "../../../assets/aftersalesservice_icons/user_manual.svg";
import ErrorCode from "../../../assets/aftersalesservice_icons/error_code.svg";
import Ticket from "../../../assets/aftersalesservice_icons/ticket.svg";
import Support from "../../../assets/aftersalesservice_icons/support.svg";
import Setting from "../../../assets/aftersalesservice_icons/setting.svg";
import Installation from "../../../assets/aftersalesservice_icons/installation.svg";
import Repair from "../../../assets/aftersalesservice_icons/repair.svg";
import {
  UserCog,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import PointRightBlue from "../../../assets/aftersalesservice_icons/pointright_blue_icon.svg";
import FallbackImg from "../../../components/FallbackImg";
import ApiService from "../../../components/Api_services";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const MenuCenter = () => {
  const navigate = useNavigate();
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [warrantyItemList, setWarrantyItemList] = useState([]);
  const [ticketCount, setTicketCount] = useState("");

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);
        // console.log("warranty info:", response);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          // ensure each item has serial_no
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest, // rest no longer contains item_warranty_period
                // map item_warranty_period -> warranty_day (ensure numeric)
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );
          setWarrantyItemList(normalizedItems);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  const serialNo1 = warrantyItemList[0]?.serial_no ?? "";
  const serialNo2 = warrantyItemList[1]?.serial_no ?? "";

  useEffect(() => {
    const fetchUnsolvedTicketCount = async () => {
      if (!serialNo1) return;

      const payload = {
        serial_no_1: serialNo1,
        serial_no_2: serialNo2,
      };

      try {
        const response = await ApiService.getUnsolvedTicketCount(payload);

        if (response && response.status === 200) {
          setTicketCount(response.data.count);
        }
      } catch (err) {
        console.error("Error fetching ticket count:", err);
      }
    };

    fetchUnsolvedTicketCount();
  }, [serialNo1, serialNo2]);

  return (
    <AfterSalesLayout>
      <div className="as-menu-center-content" id="menu_center_modal">
        <div className="as-sub-container">
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-welcome-box">
              <button
                className="as-new-install-cancel"
                onClick={() =>
                  navigate(
                    `/aftersalesservice${buildAfterSalesQuery(searchParams)}`,
                  )
                }
              >
                <ChevronLeft size={20} />
                {trans.cancel}
              </button>

              <div className="as-welcome-box-inner">
                <FallbackImg src={Shield2} alt="shield2" />
                <div className="as-welcome-box-text">
                  <p>{trans.welcome}</p>
                  <p>{trans.welcome_sub}</p>
                </div>
              </div>
            </div>

            {/* Help Center */}
            <div className="as-section-title as-orange">
              <FallbackImg src={HelpCenter} alt="helpcenter" />
              {trans.help_center}
            </div>
            <div className="as-menu-center-card">
              <Link
                to={`/aftersalesservice/checkwarrantystatus${buildAfterSalesQuery(searchParams)}`}
              >
                <div className="as-item" id="check_warranty_status_btn">
                  <div className="as-left">
                    <FallbackImg src={WarrantyStatus} alt="warrantystatus" />
                    <span>{trans.check_warr}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>

              <Link
                to={`/aftersalesservice/warrantypolicy${buildAfterSalesQuery(searchParams)}`}
              >
                <div className="as-item" id="warranty_policy_btn">
                  <div className="as-left">
                    <FallbackImg src={Policy} alt="policy" />
                    <span>{trans.warr_policy}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>

              <Link
                to={`/aftersalesservice/usermanual${buildAfterSalesQuery(searchParams)}`}
              >
                <div className="as-item" id="user_manual_btn">
                  <div className="as-left">
                    <FallbackImg src={UserManual} alt="usermanual" />
                    <span>{trans.user_manual}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>

              <Link
                to={`/aftersalesservice/errorcode${buildAfterSalesQuery(searchParams)}`}
              >
                <div className="as-item" id="error_code_btn">
                  <div className="as-left">
                    <FallbackImg src={ErrorCode} alt="errorcode" />
                    <span>{trans.error_code_guide}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>

              {/* <?php
                    // Extract indoor serial number from warranty items
                    $sn_param = '';
                    $indoor_serial = '';
                    
                    // Check if warranty_item_list exists in data
                    if (isset($data['warranty_item_list']) && is_array($data['warranty_item_list'])) {
                        foreach ($data['warranty_item_list'] as $item) {
                            if (isset($item['type']) && $item['type'] === 'INDOOR' && isset($item['serial_no'])) {
                                $indoor_serial = $item['serial_no'];
                                break;
                            }
                        }
                    }
                    
                    // Build the parameter string - encode serial number to base64
                    if ($indoor_serial) {
                        $sn_param = 'sn=' . base64_encode($indoor_serial);
                    }
                    
                    // Add ticket_number if it exists in GET parameters
                    if (isset($_GET['ticket_number'])) {
                        $sn_param .= ($sn_param ? '&' : '') . 'ticket_number=' . urlencode($_GET['ticket_number']);
                    }
                ?> */}

              {/* <div className="as-item" onclick="window.location.href='<?= base_url() . "aftersalesservice/check_my_ticket?" . $sn_param; ?>'; "> */}
              <Link
                to={`/aftersalesservice/myticket${buildAfterSalesQuery(searchParams)}`}
              >
                <div className="as-item">
                  <div className="as-left">
                    <FallbackImg src={Ticket} alt="ticket" />
                    <span>{trans.check_tickets}</span>
                  </div>
                  <div className="as-unsolved-tag-container">
                    {ticketCount > 0 && (
                      <div className="as-unsolved-tag" id="unsolved_tag">
                        {ticketCount}
                      </div>
                    )}
                    <FallbackImg src={PointRight} alt="pointright" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Support Services */}
            <div className="as-section-title as-green">
              <FallbackImg src={Support} alt="support" />
              {trans.sup_serv}
            </div>
            <div className="as-menu-center-card">
              <Link
                to={`/aftersalesservice/ticketsubmission${buildAfterSalesQuery(searchParams, { type: "General_Maintenance" })}`}
              >
                <div className="as-item" id="general_maintenance_btn">
                  <div className="as-left">
                    <FallbackImg src={Setting} alt="setting" />
                    <span>{trans.general_maint}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>

              {/* <Link
                to={`/aftersalesservice/ticketsubmission?sn=${btoa(getSerialNo())}&type=Installation`}
              >
                <div className="as-item" id="installation_btn">
                  <div className="as-left">
                    <FallbackImg src={Installation} alt="installation" />
                    <span>{trans.install}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link> */}

              <Link
                to={`/aftersalesservice/ticketsubmission${buildAfterSalesQuery(searchParams, { type: "Repair_Service" })}`}
              >
                <div className="as-item" id="repair_service_btn">
                  <div className="as-left">
                    <FallbackImg src={Repair} alt="repair" />
                    <span>{trans.repair_serv}</span>
                  </div>
                  <FallbackImg src={PointRight} alt="pointright" />
                </div>
              </Link>
            </div>

            {/* Request Support */}
            <Link
              to={`/aftersalesservice/ticketsubmission${buildAfterSalesQuery(searchParams, { type: "Technical_Consultation_Inquiry" })}`}
            >
              <div className="as-support-box">
                <div className="as-support-info">
                  <div className="as-support-icon">
                    <MessageCircle size={28} />
                  </div>
                  <div className="as-text">
                    <p className="as-title">{trans.req_sup}</p>
                    <p className="as-subtitle">{trans.req_sup_sub}</p>
                  </div>
                </div>
                <div className="as-support-chevron">
                  <ChevronRight size={24} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default MenuCenter;
